import * as t from 'io-ts';
import {isString, keyBy} from 'lodash';

import {AnalyticsReportsEnum, ReportFilterOptionsEnum} from '../../analytics/helpers/analyticsReportHelpers';
import {AnalyticsSavedView} from '../../store/panels/analytics/views/analyticsPanelViewsState';
import {parseAccount, RAccount} from './accountModel';
import {RContributingSegmentsOptionsDirty} from './analyticsMetricDetailsModel';
import {RAnalyticsMetricsKeysDirty} from './analyticsMetricsModel';
import {makeApiType, makeEnumRuntimeType} from './apiTypes';
import {parseInbox, RInbox} from './inboxModel';
import {parseTag, RTag} from './tagModel';

/**
 * Analytics models and parsers for saved views and filters.
 */

export enum AnalyticsFilterTypesEnum {
  ACCOUNTS = 'accounts',
  INBOXES = 'inboxes',
  TAGS = 'tags',
  EXCLUDE_TAGS = 'exclude_tags',
  WITHOUT_TAGS = 'without_tags',
  FULL_TAGS = 'full_tags',
  CHANNELS = 'channels',
  TEAMMATES = 'teammates',
  TEAMS = 'teams',
}

export enum AnalyticsFilterRelationsEnum {
  AND = 'and',
  OR = 'or',
}

export enum AnalyticsTagModesEnum {
  TAG_STILL_ON_CONV = 'tag_still_on_conv',
  TAG_WAS_SET = 'tag_was_set',
}

export enum AnalyticsInboxScopeFiltersEnum {
  ALL_INBOXES = 'all_inboxes',
  ALL_PRIVATE_INBOXES = 'all_private_inboxes',
  ALL_SHARED_INBOXES = 'all_shared_inboxes',
}

export enum SchedulingFrequenciesEnum {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

/*
 * Runtime type.
 */

const RAnalyticsSavedViewTermWithIdsDirty = t.type({
  type: makeEnumRuntimeType(AnalyticsFilterTypesEnum),
  relation: makeEnumRuntimeType(AnalyticsFilterRelationsEnum),
  ids: t.readonlyArray(t.number),
  negated: t.boolean,
});

const RAnalyticsSavedViewTermWithoutTagsDirty = t.type({
  type: t.literal(AnalyticsFilterTypesEnum.WITHOUT_TAGS),
  negated: t.boolean,
});

export const RAnalyticsInboxScopeFiltersEnum = makeEnumRuntimeType(AnalyticsInboxScopeFiltersEnum);

const RAnalyticsSavedViewInboxScopeTerm = t.type({
  type: t.literal(AnalyticsFilterTypesEnum.INBOXES),
  special_value: RAnalyticsInboxScopeFiltersEnum,
});

export const RAnalyticsSavedViewTermDirty = t.union([
  RAnalyticsSavedViewTermWithIdsDirty,
  RAnalyticsSavedViewTermWithoutTagsDirty,
  RAnalyticsSavedViewInboxScopeTerm,
]);

export const RAnalyticsSelectedMetricsModelDirty = t.record(t.string, t.readonlyArray(t.string));

const RAnalyticsOptionsBucketBoundariesDirty = t.record(t.string, t.array(t.number));

export const RAnalyticsReportFilterOptionsDirty = t.partial({
  [ReportFilterOptionsEnum.BUCKET_BOUNDARIES]: RAnalyticsOptionsBucketBoundariesDirty,
  [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: RContributingSegmentsOptionsDirty,
  [ReportFilterOptionsEnum.SELECTED_METRICS]: RAnalyticsSelectedMetricsModelDirty,
  [ReportFilterOptionsEnum.ALWAYS_EMIT_CONTACT_ID]: t.boolean,
  [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: t.array(RAnalyticsMetricsKeysDirty),
  [ReportFilterOptionsEnum.TAG_MODE]: makeEnumRuntimeType(AnalyticsTagModesEnum),
});

const RAnalyticsSavedViewTermsDirty = t.readonlyArray(RAnalyticsSavedViewTermDirty);

export const RAnalyticsSavedViewFiltersDirty = t.intersection([
  t.type({
    terms: RAnalyticsSavedViewTermsDirty,
  }),
  t.partial({
    options: RAnalyticsReportFilterOptionsDirty,
  }),
]);

// Tags only contain name, we need more to handle all kinds of icon, that's why we use fullTags instead.
export const RAnalyticsSavedViewMetadataDirty = t.type({
  tags: t.record(t.string, t.string), // @TODO: Delete tags and use fullTags instead.
  fullTags: t.record(t.string, RTag),
  hasAllInboxes: t.boolean,
  inboxes: t.record(t.string, RInbox),
  accounts: t.record(t.string, RAccount),
});

const RSchedulingSectionDirty = t.type({
  stage: makeEnumRuntimeType(AnalyticsReportsEnum),
  metrics: t.array(
    t.intersection([
      t.type({
        id: t.string,
      }),
      t.partial({
        columns: t.array(t.string),
      }),
    ]),
  ),
});

export const RSchedulingConfigDirty = t.type({
  recipients: t.array(t.string),
  subject: t.string,
  message: t.union([t.string, t.undefined]),
  sections: t.array(RSchedulingSectionDirty),
  reportType: makeEnumRuntimeType(AnalyticsReportsEnum),
  frequency: makeEnumRuntimeType(SchedulingFrequenciesEnum),
  time_zone: t.string,
});

// This is coming from the API.
const RAnalyticsSavedViewApiDirty = t.readonly(
  t.type({
    id: t.number,
    name: t.string,
    namespace: t.string,
    visibility_namespace: t.string,
    filters: RAnalyticsSavedViewFiltersDirty,
    metadata: RAnalyticsSavedViewMetadataDirty,
    scheduling_config: t.union([RSchedulingConfigDirty, t.null]),
  }),
);

const RAnalyticsSavedViewsApiDirty = t.readonlyArray(RAnalyticsSavedViewApiDirty);

// This is what we use in `front-client`.
const RAnalyticsSavedViewDirty = t.readonly(
  t.type({
    id: t.number,
    name: t.string,
    filters: RAnalyticsSavedViewFiltersDirty,
    metadata: RAnalyticsSavedViewMetadataDirty,
    namespace: t.string,
    // Explanation of visibility namespace vs. namespace:
    // https://paper.dropbox.com/doc/BgVQW2dMnIQCjBTvzAX82~i9Ag-VFKE8bAl6ComWco9U1PM2
    visibilityNamespace: t.string,
    schedulingConfig: t.union([RSchedulingConfigDirty, t.null]),
  }),
);

const RAnalyticsSavedViewsDirty = t.readonlyArray(RAnalyticsSavedViewDirty);

/*
 * Type.
 */

export interface SchedulingSection extends t.TypeOf<typeof RSchedulingSectionDirty> {}

export interface SchedulingConfig extends t.TypeOf<typeof RSchedulingConfigDirty> {}

export interface AnalyticsSavedViewTermWithIds extends t.TypeOf<typeof RAnalyticsSavedViewTermWithIdsDirty> {}
interface AnalyticsSavedViewWithoutTagsTerm
  extends t.TypeOf<typeof RAnalyticsSavedViewTermWithoutTagsDirty> {}
export interface AnalyticsSavedViewInboxScopeTerm
  extends t.TypeOf<typeof RAnalyticsSavedViewInboxScopeTerm> {}
export type AnalyticsSavedViewTerm = t.TypeOf<typeof RAnalyticsSavedViewTermDirty>;
export type AnalyticsSavedViewTerms = t.TypeOf<typeof RAnalyticsSavedViewTermsDirty>;

export interface AnalyticsSavedViewFiltersModel extends t.TypeOf<typeof RAnalyticsSavedViewFiltersDirty> {}

export type AnalyticsSelectedMetricsModel = t.TypeOf<typeof RAnalyticsSelectedMetricsModelDirty>;
export type AnalyticsReportFilterOptions = t.TypeOf<typeof RAnalyticsReportFilterOptionsDirty>;
export type AnalyticsOptionsBucketBoundaries = t.TypeOf<typeof RAnalyticsOptionsBucketBoundariesDirty>;

interface AnalyticsSavedViewApiModel extends t.TypeOf<typeof RAnalyticsSavedViewApiDirty> {}
interface AnalyticsSavedViewsApiModel extends t.TypeOf<typeof RAnalyticsSavedViewsApiDirty> {}

export interface AnalyticsSavedViewModel extends t.TypeOf<typeof RAnalyticsSavedViewDirty> {}
export interface AnalyticsSavedViewsModel extends t.TypeOf<typeof RAnalyticsSavedViewsDirty> {}

/*
 * IO methods.
 */

function parseAnalyticsSavedView(src: AnalyticsSavedViewApiModel): AnalyticsSavedViewModel {
  return {
    id: src.id,
    name: src.name,
    namespace: src.namespace,
    filters: src.filters,
    metadata: parseAnalyticsSavedViewMetadata(src.metadata),
    visibilityNamespace: src.visibility_namespace,
    schedulingConfig: src.scheduling_config,
  };
}

function parseAnalyticsSavedViewMetadata(src: any): AnalyticsSavedViewModel['metadata'] {
  return {
    tags: src.tags,
    fullTags: keyBy((src.full_tags || []).map(parseTag), 'id'),
    inboxes: keyBy((src.inboxes || []).map(parseInbox), 'id'),
    accounts: keyBy((src.accounts || []).map(parseAccount), 'id'),
    hasAllInboxes: src.has_all_inboxes,
  };
}

/*
 * Serialize a base Saved View payload, for creation.
 */

export function serializeBaseAnalyticsSavedView(
  savedView: Omit<AnalyticsSavedView, 'id' | 'isDisplayed' | 'metadata'>,
  namespace: string,
): Omit<AnalyticsSavedViewApiModel, 'id' | 'metadata'> {
  const {name, filters, visibilityNamespace, schedulingConfig} = savedView;

  if (!isString(visibilityNamespace) || visibilityNamespace.length === 0) {
    throw new Error(
      `Error when serializing Saved View with visibilityNamespace: ${visibilityNamespace}. Should be a non-empty string.`,
    );
  }

  return {
    name,
    namespace,
    filters,
    visibility_namespace: visibilityNamespace,
    scheduling_config: schedulingConfig,
  };
}

/*
 * Serialize a full Saved View payload, for updates.
 */

export function serializeAnalyticsSavedView(
  savedView: AnalyticsSavedView,
  namespace: string,
): Omit<AnalyticsSavedViewApiModel, 'metadata'> {
  const baseView = serializeBaseAnalyticsSavedView(savedView, namespace);

  if (typeof savedView.id !== 'number') {
    throw new Error(`Error when serializing Saved View with id: ${savedView.id}. Should be a number.`);
  }

  return {
    ...baseView,
    id: savedView.id,
  };
}

function parseAnalyticsSavedViews(src: {
  'anltcs/views': AnalyticsSavedViewsApiModel;
}): AnalyticsSavedViewsModel {
  return src['anltcs/views'].map(parseAnalyticsSavedView);
}

/*
 * API type.
 */

export const ApiAnalyticsSavedView = makeApiType(
  'analyticsSavedView',
  RAnalyticsSavedViewDirty,
  parseAnalyticsSavedView,
);

export const ApiAnalyticsSavedViews = makeApiType(
  'analyticsSavedViews',
  RAnalyticsSavedViewsDirty,
  parseAnalyticsSavedViews,
);

/*
 * Type guards.
 */

export function isTermWithoutTags(term: AnalyticsSavedViewTerm): term is AnalyticsSavedViewWithoutTagsTerm {
  return term.type === 'without_tags';
}

export function isInboxScopeTerm(term: AnalyticsSavedViewTerm): term is AnalyticsSavedViewInboxScopeTerm {
  return term.type === 'inboxes' && 'special_value' in term;
}

export function isTermNotWithoutTags(term: AnalyticsSavedViewTerm): term is AnalyticsSavedViewTermWithIds {
  return !isTermWithoutTags(term);
}

export function isTermWithoutIds(
  term: AnalyticsSavedViewTerm,
): term is AnalyticsSavedViewWithoutTagsTerm | AnalyticsSavedViewInboxScopeTerm {
  return isTermWithoutTags(term) || isInboxScopeTerm(term);
}

export function isTermWithIds(term: AnalyticsSavedViewTerm): term is AnalyticsSavedViewTermWithIds {
  return !isTermWithoutIds(term);
}

export function isTagTerm(term: AnalyticsSavedViewTerm): term is AnalyticsSavedViewTermWithIds {
  return term.type === AnalyticsFilterTypesEnum.TAGS && !term.negated;
}

export function isFilterTypeWithDropdown(type: AnalyticsFilterTypesEnum) {
  return [
    AnalyticsFilterTypesEnum.ACCOUNTS,
    AnalyticsFilterTypesEnum.TAGS,
    AnalyticsFilterTypesEnum.INBOXES,
    AnalyticsFilterTypesEnum.CHANNELS,
    AnalyticsFilterTypesEnum.EXCLUDE_TAGS,
    AnalyticsFilterTypesEnum.TEAMMATES,
    AnalyticsFilterTypesEnum.TEAMS,
  ].includes(type);
}
